import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Play, Pause, X, SkipForward, RotateCcw, Check, Sunrise, Heart } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Format seconds as m:ss */
function fmt(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

/**
 * MorningYoga — 10-minute guided yoga + meditation flow.
 * Props:
 *   onClose: () => void
 *   onComplete: () => void  (optional; called when user finishes)
 */
export default function MorningYoga({ onClose, onComplete }) {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(-1); // -1 = intro, length = outro/finished
  const [remaining, setRemaining] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const tickRef = useRef(null);
  const chimeRef = useRef(null);

  // Load sequence
  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/body/morning-yoga`, { withCredentials: true });
        setData(d);
      } catch (e) {
        toast.error('Kunde inte ladda yogan');
      }
    })();
  }, []);

  // Prepare chime (short beep using WebAudio)
  const playChime = () => {
    if (muted) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      if (!chimeRef.current) chimeRef.current = new Ctx();
      const ctx = chimeRef.current;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = 528; // solfeggio
      g.gain.value = 0.0001;
      o.connect(g); g.connect(ctx.destination);
      const t = ctx.currentTime;
      g.gain.linearRampToValueAtTime(0.15, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 1.2);
      o.start(t);
      o.stop(t + 1.3);
    } catch (e) { /* ignore */ }
  };

  // Main tick loop
  useEffect(() => {
    if (!playing || !data) return;
    tickRef.current = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) {
          // advance
          clearInterval(tickRef.current);
          return 0;
        }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [playing, idx, data]);

  // When remaining hits 0, advance
  useEffect(() => {
    if (!playing || !data) return;
    if (remaining !== 0) return;
    // advance to next pose
    playChime();
    if (idx < 0) {
      // leaving intro
      setIdx(0);
      setRemaining(data.poses[0].duration);
    } else if (idx < data.poses.length - 1) {
      const next = idx + 1;
      setIdx(next);
      setRemaining(data.poses[next].duration);
    } else {
      // All poses done → outro
      setIdx(data.poses.length);
      setPlaying(false);
    }
  }, [remaining, playing]);

  const start = () => {
    if (!data) return;
    if (idx === -1) {
      setIdx(0);
      setRemaining(data.poses[0].duration);
    } else if (idx >= data.poses.length) {
      // finished, restart
      setIdx(0);
      setRemaining(data.poses[0].duration);
    }
    setPlaying(true);
  };
  const pause = () => setPlaying(false);
  const skip = () => {
    if (!data) return;
    if (idx < 0) {
      setIdx(0); setRemaining(data.poses[0].duration); return;
    }
    if (idx < data.poses.length - 1) {
      setIdx(idx + 1);
      setRemaining(data.poses[idx + 1].duration);
    } else {
      setIdx(data.poses.length);
      setRemaining(0);
      setPlaying(false);
    }
  };
  const restart = () => {
    setIdx(-1);
    setRemaining(0);
    setPlaying(false);
  };

  const complete = async () => {
    try {
      const { data: resp } = await axios.post(
        `${API}/body/morning-yoga/complete`, {}, { withCredentials: true }
      );
      if (resp.already_completed) {
        toast('💛 Redan klart idag');
      } else {
        toast.success(`🌸 +${resp.points_awarded}p · Fin start på dagen`);
      }
      if (onComplete) onComplete();
      onClose?.();
    } catch (e) {
      toast.error('Kunde inte spara — men du gjorde det ändå 💛');
      onClose?.();
    }
  };

  if (!data) {
    return (
      <div className="fixed inset-0 z-[220] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="text-white font-bold">Laddar...</div>
      </div>
    );
  }

  const finished = idx >= data.poses.length;
  const current = idx >= 0 && idx < data.poses.length ? data.poses[idx] : null;
  const total = data.poses.length;
  const pct = idx < 0 ? 0 : finished ? 100 : Math.round(((idx + 1) / total) * 100);

  return (
    <div
      className="fixed inset-0 z-[220] bg-gradient-to-br from-[#FFE8F5] via-[#F5EEFF] to-[#E8F0FF] overflow-auto"
      data-testid="morning-yoga-modal"
    >
      {/* Header */}
      <div className="sticky top-0 bg-white/70 backdrop-blur-md border-b-[3px] border-black p-3 flex items-center justify-between gap-2 z-10">
        <div className="flex items-center gap-2">
          <Sunrise className="w-5 h-5" />
          <div>
            <div className="font-black text-sm leading-tight">{data.title}</div>
            <div className="text-[10px] opacity-60">{data.subtitle}</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setMuted(!muted)}
            className="neo-btn px-2 py-1 bg-white text-xs"
            title={muted ? 'Slå på ljud' : 'Stäng av ljud'}
            data-testid="yoga-mute-btn"
          >
            {muted ? '🔇' : '🔔'}
          </button>
          <button
            onClick={onClose}
            className="neo-btn p-1.5 bg-white"
            data-testid="yoga-close-btn"
            aria-label="Stäng"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-4 pt-3">
        <div className="h-2 w-full border-[2px] border-black rounded-full overflow-hidden bg-white">
          <div
            className="h-full transition-all duration-500"
            style={{
              width: `${pct}%`,
              background: 'linear-gradient(90deg, #FFC8DD, #C8B6FF, #A2D2FF)',
            }}
          />
        </div>
        <div className="text-xs text-center opacity-60 mt-1">
          {idx < 0 ? 'Intro' : finished ? 'Klart ✨' : `Position ${idx + 1} / ${total}`}
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-2xl mx-auto p-4 pb-24">
        {idx === -1 && (
          <div className="neo-card p-6 bg-white text-center space-y-4" data-testid="yoga-intro">
            <div className="text-6xl">🌅</div>
            <h2 className="font-black text-xl">Välkommen till morgonen</h2>
            <p className="text-sm opacity-85 leading-relaxed italic">"{data.intro}"</p>
            <button
              onClick={start}
              className="neo-btn px-6 py-3 bg-[#B9FBC0] font-bold flex items-center gap-2 mx-auto"
              data-testid="yoga-start-btn"
            >
              <Play className="w-5 h-5" /> Starta
            </button>
            <p className="text-[11px] opacity-60">10 min · du kan pausa eller hoppa när du vill</p>
          </div>
        )}

        {current && (
          <div className="neo-card p-6 bg-white space-y-4 text-center" data-testid="yoga-pose">
            <div className="text-7xl animate-[pulse_3s_ease-in-out_infinite]">{current.emoji}</div>
            <div>
              <div className="text-[10px] font-bold uppercase opacity-60 tracking-wider">
                {current.name.includes('intention') ? 'Avslutning' : `Position ${idx + 1}`}
              </div>
              <h2 className="font-black text-2xl leading-tight mt-1">{current.name}</h2>
            </div>

            {/* Timer */}
            <div className="flex items-center justify-center">
              <div className="w-24 h-24 rounded-full border-[4px] border-black flex items-center justify-center bg-gradient-to-br from-[#FFE8F5] to-[#E8F0FF] shadow-[4px_4px_0_#000]">
                <span className="text-3xl font-black" data-testid="yoga-timer">{fmt(remaining)}</span>
              </div>
            </div>

            {/* Breath */}
            <div className="neo-card p-2 bg-[#FFE8A3] inline-block mx-auto">
              <span className="text-xs font-bold">🌬️ {current.breath}</span>
            </div>

            {/* Instruction */}
            <p className="text-sm leading-relaxed">{current.instruction}</p>

            {/* Meaning */}
            <p className="text-xs opacity-70 italic border-t border-black/10 pt-2">
              💫 {current.meaning}
            </p>

            {/* Controls */}
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
                onClick={restart}
                className="neo-btn px-3 py-2 bg-white text-xs flex items-center gap-1"
                data-testid="yoga-restart-btn"
                title="Börja om"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                onClick={playing ? pause : start}
                className="neo-btn px-5 py-3 bg-[#C8B6FF] font-bold flex items-center gap-2"
                data-testid="yoga-playpause-btn"
              >
                {playing ? <><Pause className="w-4 h-4" /> Paus</> : <><Play className="w-4 h-4" /> Spela</>}
              </button>
              <button
                onClick={skip}
                className="neo-btn px-3 py-2 bg-white text-xs flex items-center gap-1"
                data-testid="yoga-skip-btn"
                title="Nästa"
              >
                <SkipForward className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {finished && (
          <div className="neo-card p-6 bg-gradient-to-br from-[#B9FBC0] via-[#FFE8F0] to-[#C8B6FF] text-center space-y-4" data-testid="yoga-outro">
            <div className="text-6xl">🌸</div>
            <h2 className="font-black text-xl">Du är färdig</h2>
            <p className="text-sm opacity-85 leading-relaxed italic">"{data.outro}"</p>
            <div className="flex items-center justify-center gap-2 flex-wrap pt-2">
              <button
                onClick={complete}
                className="neo-btn px-5 py-3 bg-white font-bold flex items-center gap-2"
                data-testid="yoga-finish-btn"
              >
                <Check className="w-5 h-5" /> Markera klart (+5p)
              </button>
              <button
                onClick={restart}
                className="neo-btn px-4 py-3 bg-white text-sm flex items-center gap-2"
                data-testid="yoga-again-btn"
              >
                <RotateCcw className="w-4 h-4" /> Kör igen
              </button>
            </div>
            <p className="text-[11px] opacity-60 flex items-center justify-center gap-1">
              <Heart className="w-3 h-3 fill-current" /> Inget du inte redan är
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
